import { ApiResult } from 'src/Libs/api'
import { handleFetchError } from 'src/Libs/api/utils'
import { UnexpectedApiResponseError } from 'src/Libs/errors'
import { StatusCodes } from 'src/Libs/http-status-codes'
import { AuthFetchWithResponse } from 'src/Types/api'
import { AuthFetchError } from '@e3dc-react/shell/Types/api'
import { GetFileDto, GetFilesDto } from '../types'
import { FileDto } from '../dto'
import { File } from '../models'
import { fromFileDtoToModel } from '../transformers/from-file-dto-to-model.transformer'

/**
 * Function to upload a file (usualy an image) to an installation statement of specific installation
 */
export const uploadInstallationStatementFile = async (
  deviceLinkId: number,
  installationId: number,
  fileObject: FormData,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<number>> => {
  try {
    const response = await authFetch<number>({
      path: `installations/${installationId}/device-links/${deviceLinkId}/statements/current/files`,
      method: 'POST',
      payload: fileObject,
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
    })
    if (response.status !== StatusCodes.CREATED) {
      throw new UnexpectedApiResponseError(response.status)
    }
    return new ApiResult<number>(response.data, false)
  } catch (error) {
    return handleFetchError<number>(error)
  }
}

/**
 * A function which gets a thumbnail of an image file from an installation statement of specific installation
 */
export const getFileThumbnail = async (installationId: number, fileId: number, authFetch: AuthFetchWithResponse): Promise<ApiResult<string>> => {
  try {
    const response = await authFetch<BlobPart>({
      path: `installations/${installationId}/files/${fileId}/preview`,
      method: 'GET',
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      type: 'blob',
    })
    if (response.status !== StatusCodes.OK) {
      throw new UnexpectedApiResponseError(response.status)
    }
    return new ApiResult<string>(response.data as string, false)
  } catch (error) {
    return handleFetchError<string>(error)
  }
}

export const getCurrentFiles = async (
  deviceLinkId: number,
  installationId: number,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<GetFilesDto>> => {
  try {
    const response = await authFetch<GetFilesDto>({
      path: `installations/${installationId}/device-links/${deviceLinkId}/statements/current/files`,
      method: 'GET',
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
    })

    if (response.status === StatusCodes.OK) {
      return new ApiResult<GetFilesDto, AuthFetchError | Error>(response.data)
    }

    if (response.status === StatusCodes.NO_CONTENT) {
      return new ApiResult<GetFilesDto, AuthFetchError | Error>([])
    }

    return new ApiResult<GetFilesDto, AuthFetchError | Error>(null, true)
  } catch (error) {
    return handleFetchError<GetFilesDto>(error)
  }
}

export const getFile = async (installationId: number, fileId: number, authFetch: AuthFetchWithResponse): Promise<ApiResult<GetFileDto>> => {
  try {
    const response = await authFetch<GetFileDto>({
      path: `installations/${installationId}/files/${fileId}`,
      method: 'GET',
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
    })

    if (response.status === StatusCodes.OK) {
      return new ApiResult<GetFileDto, AuthFetchError | Error>(response.data)
    }

    return new ApiResult<GetFileDto, AuthFetchError | Error>(null, true)
  } catch (error) {
    return handleFetchError<GetFileDto>(error)
  }
}

export const deleteFile = async (installationId: number, fileId: number, authFetch: AuthFetchWithResponse): Promise<ApiResult<boolean>> => {
  try {
    const response = await authFetch<boolean>({
      path: `installations/${installationId}/files/${fileId}`,
      method: 'DELETE',
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
    })

    if (response.status === StatusCodes.OK) {
      return new ApiResult<boolean, AuthFetchError | Error>(response.data)
    }

    return new ApiResult<boolean, AuthFetchError | Error>(null, true)
  } catch (error) {
    return handleFetchError<boolean>(error)
  }
}

type GetFilesParams = {
  installationId?: number
  deviceLinkId: number
  authFetch: AuthFetchWithResponse
  filter?: string
}

export const getFiles = async ({ authFetch, deviceLinkId, filter, installationId }: GetFilesParams): Promise<ApiResult<File[]>> => {
  try {
    const response = await authFetch<FileDto[]>({
      path: `installations/${installationId}/device-links/${deviceLinkId}/statements/files`,
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      payload: {
        filter,
      },
    })

    if (response.status === StatusCodes.OK) {
      const modelData = fromFileDtoToModel(response.data)

      return new ApiResult<File[], AuthFetchError | Error>(modelData)
    }

    return new ApiResult<File[], AuthFetchError | Error>(null, true)
  } catch (error) {
    return handleFetchError<File[]>(error)
  }
}
